import React, { useEffect } from 'react';
import { Col, Row, message } from 'antd';
import { SelectOutlined } from '@ant-design/icons';
import { OverlayAO3DataList, OverlayBookstatDataList, OverlayRoyalRoadDataList }
  from '../../core/constants/global-constants';
import { AMAZON, TAB_NAME } from '../../core/constants';
import Endpoints, { AO3, ROYAL_ROAD_TITLE } from '../../core/constants/endpoints';
import AlsoBought from '../AlsoBought';
import RatingDistribution from '../RatingDistribution';
import styled from 'styled-components';
import './styles.css';
import { useGetBookDetails } from '../../core/modules/book/hooks/useGetBookDetails';
import { useClosedDealNotes } from '../../core/modules/book/hooks/useClosedDealNotes';
import PredictionsGraph from './PredictionsGraph';
import OverlayActions from './OverlayActions';
import OverlayHeader from './OverlayHeader';
import OverlayTable from './OverlayTable';
import OverlayTags from './OverlayTags';
import OverlayClosedDealNotes from './OverlayClosedDealNotes';
import TableauEmbed from './TableauEmbed';
import OverlayTableauLink from './OverlayTableauLink';

interface IOverlayContentProps {
  book: object;
  tabId: string;
  refetchTable: Function;
}

const Dot = styled.span`
    width: 15px;
    height: 15px;
    display: inline-block;
    border-radius: 50%;
    margin-right: 8px;
    border: 2px solid white;
    background-color: ${({ color }) => color};
`;

const tagColors = ['#5b89c2', '#c95c57', '#8108b3', '#008028', '#1dc6cc'];

const OverlayContent: React.FC<IOverlayContentProps> = ({ book, tabId, refetchTable }) => {
  const { data, isLoading, refetchBookDetails } = useGetBookDetails(
    book['ASIN']
  );
  const authorName = book['Author'];

  const { data: closedDealNotesData, isLoading: isClosedDealNotesLoading }
    = useClosedDealNotes(authorName);

  useEffect(() => {
    if (book['ASIN']) {
      refetchBookDetails();
    }
  }, [book]);

  const [messageApi, contextHolder] = message.useMessage();
  book['Current Tab'] = TAB_NAME[tabId];

  let filteredKeys: string[] = [];
  let source = 'bookstat';

  if (window.location.pathname.includes('bookstat')) {
    filteredKeys = OverlayBookstatDataList.filter((key) => Object.prototype.hasOwnProperty.call(book, key));
    source = 'bookstat';
  } else if (window.location.pathname.includes('royal-road')) {
    filteredKeys = OverlayRoyalRoadDataList.filter((key) => Object.prototype.hasOwnProperty.call(book, key));
    source = 'royal-road';
  } else if (window.location.pathname.includes('ao3')) {
    filteredKeys = OverlayAO3DataList.filter((key) => Object.prototype.hasOwnProperty.call(book, key));
    source = 'ao3';
  }

  const zoomedCoverImage = (
    <div>
      <img
        alt={book['Title']}
        src={book['largecoverimageurl']}
        className='overlay-zoomed-img'
      />
    </div>
  );

  const handleCopyUrl = () => {
    let book_id = '';

    if (window.location.pathname.includes('bookstat')) {
      book_id = `amazonasin=${book['ASIN']}`;
    } else if (window.location.pathname.includes('royal-road')) {
      book_id = `fiction_id=${book['Fiction Id']}`;
    } else if (window.location.pathname.includes('ao3')) {
      book_id = `work_id=${book['work_id']}`;
    }

    let bookUrl = window.location.href;
    const urlWithoutSearch = bookUrl.split('?')[0];
    bookUrl = `${urlWithoutSearch}?${book_id}`;
    message.config({ top: 10, maxCount: 3, rtl: false, duration: 1 });
    navigator.clipboard.writeText(bookUrl).then(() => {
      messageApi.open({
        type: 'success',
        content: 'Copied link to clipboard.',
        duration: 1
      });
    });
    message.destroy();
  };

  const customColumn = (key: string) => {
    switch (key) {
    case 'ASIN' :
      return (
        <div>
          <a
            className='table-cell-asin'
            href={`${Endpoints[AMAZON]}${book[key]}`}
            rel='noreferrer'
            target='_blank'
          >
            {book[key]} <SelectOutlined />
          </a>
        </div>
      );
    case 'Fiction Id' :
      return (
        <div>
          <a
            className='table-cell-asin'
            href={`${Endpoints[ROYAL_ROAD_TITLE]}${book[key]}`}
            rel='noreferrer'
            target='_blank'
          >
            {book[key]} <SelectOutlined />
          </a>
        </div>
      );
    case 'Podium Hit Count' :
      return (
        <div className='table-cell-asin'>
          <AlsoBought
            podiumHitCount={book[key] === null ? -1 : book[key]}
            asin={book['ASIN']}
          />
        </div>
      );
    case 'Amzn Rating' :
      return (
        <RatingDistribution rating={book[key]} book={book} />
      );
    case 'Patreon' :
      return (
        <div
          style={{ maxWidth: '20vw' }}><a
            className='table-cell-asin'
            href={`${book[key]}`}
            rel='noreferrer'
            target='_blank'
          >
            {book[key]}
          </a></div>
      );
    case 'Link' :
      return (
        <div>
          <a
            className='table-cell-asin'
            href={`${Endpoints[AO3]}${book['work_id']}`}
            rel='noreferrer'
            target='_blank'
          >
            {book['work_id']} <SelectOutlined />
          </a>
        </div>
      );
    default :
      return (
        book[key]
      );
    }
  };

  return (
    <>
      {contextHolder}
      <Row className='card-row'>
        <Col span={24}>
          <OverlayHeader
            book={book}
            zoomedCoverImage={zoomedCoverImage}
            handleCopyUrl={handleCopyUrl}
            Dot={Dot}
          />
        </Col>
      </Row>

      <Row>
        <Col span={24} className='overlay-actions-row'>
          <OverlayActions book={book} TAB_ID={tabId} refetchTable={refetchTable} />
        </Col>
      </Row>

      <OverlayTags book={book} tagColors={tagColors} />

      <OverlayTable
        filteredKeys={filteredKeys}
        customColumn={customColumn}
      />

      <OverlayClosedDealNotes
        authorName={authorName}
        isClosedDealNotesLoading={isClosedDealNotesLoading}
        closedDealNotesData={closedDealNotesData}
      />

      {/* Phase 2: inline embedded dashboard — only renders when flag is true, Author specific */}
      { source !== 'ao3' && <TableauEmbed authorName={authorName} height={550} />}

      {/* Redirect to the full author dashboard in Tableau (same view as the embed above) */}
      { source !== 'ao3' && <OverlayTableauLink authorName={authorName} />}

      <PredictionsGraph
        data={data?.data || []}
        isLoading={isLoading}
        asin={book['ASIN']}
      />
    </>
  );
};

export default OverlayContent;